import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";

import {PlanningDragAndDropService} from "../../services/planning-drag-and-drop.service";
import {TableColumnSettings} from "../../../../commons/angular/services/table-column-manager";

import * as _ from "lodash";
import {PlanningViewSettingsJobsModel} from "../../../models/planning-view-settings.model";
import {DEFAULT_JOBS_COLUMN_WIDTHS} from "../../../constants/planning-column-widths.constant";
import {JOBS_COLUMN_GROUPS, JOBS_GROUPED_COLUMN_GROUPS} from "../../../constants/planning-column-groups.constant";
import {
  CdkDragDrop
} from "@angular/cdk/drag-drop";
import {PlanningPanelModel} from "../../../models/planning-panel.model";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {JobsGroupByEnum} from "../../../types/jobs-group-by.enum";
import {PlanningRouteModel} from "../../../models/planning-route.model";
import {PlanningResourceModel} from "../../../../resources/models/planning-resource.model";

import {AddressRequestType} from "../../../../addresses/angular/types/address-request.type";
import {LocationType} from "../../../../commons/types/location.type";

export enum SplitSuggestionType {
  SPLIT_PACKAGES = 'SPLIT_PACKAGES',
  SPLIT_LOCATION = 'SPLIT_LOCATION'
}

require('./planning-draft-jobs-table-list.scss');
@Component({
  standalone: false,
  selector: 'app-planning-draft-jobs-table-list',
  templateUrl: './planning-draft-jobs-table-list.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanningDraftJobsTableListComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() draft?: any;
  @Input() jobs: any;
  @Input() jobsGroupByType: JobsGroupByEnum;
  @Input() routes: PlanningRouteModel[];
  @Input() resources: PlanningResourceModel[];
  @Input() panel: PlanningPanelModel;
  @Input() panels: PlanningPanelModel[];
  @Input() selectedIds: string[];
  @Input() highlightedActivity: any;
  @Input() settings: PlanningViewSettingsJobsModel;
  @Input() expandedGroupedJobs: { [key: string]: boolean } = {};
  @Output() onSelect = new EventEmitter<string[]>();
  @Output() onMoveToLeftovers = new EventEmitter<string[]>();
  @Output() onViewJob = new EventEmitter<{ jobs?: any[], jobIds?: string[], event?: any }>();
  @Output() onToggleObligateTimeWindow = new EventEmitter<{ jobIds: string[], obligateTimeWindow: boolean }>();
  @Output() onContextMenu = new EventEmitter<{ event: any, job: any }>();
  @Output() onReverseJobSplit = new EventEmitter<{ event: MouseEvent, job: any }>();
  @Output() onGroupExpanded = new EventEmitter<{ id: string, expanded: boolean }>();
  @Output() onOpenRouteInFloatingTable = new EventEmitter<string>();
  @Output() onSettingsChange = new EventEmitter<PlanningViewSettingsJobsModel>();

  get uniqueId(): string {
    return 'jobs-' + this.panel?.id;
  }
  defaultWidths: { [key: string]: number } = DEFAULT_JOBS_COLUMN_WIDTHS;
  columnGroups = JOBS_COLUMN_GROUPS;
  groupedColumnGroups = JOBS_GROUPED_COLUMN_GROUPS;

  @ViewChild('headerContainer', {static: false}) private headerContainer: ElementRef;
  @ViewChild('bodyContainer', {read: ElementRef, static: false}) private bodyContainer: ElementRef;
  @ViewChild(CdkVirtualScrollViewport, {static: false}) private viewport: CdkVirtualScrollViewport;
  private resizeObserver: ResizeObserver;
  private scrollEndTimer: any | null = null;

  public connectedPanelIds: string[];
  public selectedKeys: Object;
  public routesByJobMap: Object;
  public isSmallScreen: boolean;
  public query: any;
  public type: AddressRequestType;
  public isShowAddressIcon: Object = {};
  public SplitSuggestionType = SplitSuggestionType;
  public routeIndexesMap: {[jobId: string]: { index: number, isHighlighted: boolean }[]} = {};
  public routeColorsMap: {[jobId: string]: string} = {};
  public isAllGroupsExpanded: boolean = false;

  constructor(
    @Inject('$timeout') private $timeout,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject(PlanningDragAndDropService) private planningDragAndDropService: PlanningDragAndDropService,
    @Inject('planningCommonService') private planningCommonService
    ) {
  }

  ngOnInit() {
    this.isSmallScreen = window.innerWidth < 1366;
    this.query = {
      draftId: this.draft?.id,
      projectId: this.draft?.projectId
    };
    this.type = AddressRequestType.Planning;

    this.resizeObserver = new ResizeObserver(() => {
      this.viewport?.checkViewportSize();
      this.syncHeaderScrollbarWidth();
    });
  }

  isHighQualityAddress(address: any): boolean {
    return !!(address && address.addressResolvingInfo &&
      address.addressResolvingInfo.qualityGroup === "High");
  }

  isValidAddressType(address: any): boolean {
    return !!(address && (address.locationType === LocationType.ACCOUNT ||
      address.locationType === LocationType.DEPOT ||
      address.locationType === LocationType.RESOURCE ||
      address.locationType === LocationType.CUSTOMER));
  }

  onToggleIsShowAddressIcon(key: string, isShow: boolean) {
    this.isShowAddressIcon[key] = isShow;
    this.cdr.detectChanges();
  }

  getIsObligatedTimeWindow(job: any): boolean {
    if (!job?.stops?.length) {
      return false;
    }
    return job.stops.some(stop => stop.obligateTimeWindow);
  }

  ngAfterViewInit() {
    if (this.bodyContainer) {
      this.resizeObserver.observe(this.bodyContainer.nativeElement);

      const innerWrapper = this.bodyContainer.nativeElement.querySelector('.table-inner-wrapper');
      if (innerWrapper) {
        this.resizeObserver.observe(innerWrapper);
      }
    }
    this.syncHeaderScrollbarWidth();

  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.scrollEndTimer) {
      clearTimeout(this.scrollEndTimer);
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.viewport?.checkViewportSize();
  }

  onBodyScroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.headerContainer) {
      this.headerContainer.nativeElement.scrollLeft = target.scrollLeft;
    }
  }

  private syncHeaderScrollbarWidth() {
    if (this.headerContainer && this.bodyContainer) {
      const bodyEl = this.bodyContainer.nativeElement;
      const headerEl = this.headerContainer.nativeElement;
      const scrollbarWidth = bodyEl.offsetWidth - bodyEl.clientWidth;
      headerEl.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  onScroll(_: Event): void {
    if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = setTimeout(() => {
      if (this.viewport) {
        const viewportEl = this.viewport.elementRef.nativeElement;
        const contentWrapper = viewportEl.querySelector('.cdk-virtual-scroll-content-wrapper');

        if (contentWrapper) {
          const viewportRect = viewportEl.getBoundingClientRect();
          const wrapperRect = contentWrapper.getBoundingClientRect();

          const isBlank = wrapperRect.height === 0 && this.jobs?.length > 0;
          const isOutOfPlace = (wrapperRect.bottom < viewportRect.top) || (wrapperRect.top > viewportRect.bottom);

          if ((isBlank || isOutOfPlace) && this.jobs?.length > 0) {
            this.viewport.checkViewportSize();
          }
        }
      }
      this.scrollEndTimer = null;
    }, 200);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.panels || changes.routes || changes.resources) {
      const filteredPanels = _.filter(this.panels, (p) => {
        return p.id !== this.panel.id;
      });
      this.connectedPanelIds = _.concat(_.map(filteredPanels, 'id'), _.map(this.routes, 'id'), _.map(this.resources, 'id'));
    }

    if (changes.selectedIds || changes.jobs) {
      this._initSelectedKeys();
    }

    if (changes.routes) {
      this.routesByJobMap = {};
      if (this.routes?.length) {
        this.routes.forEach((r) => {
          r.jobIds.forEach((id) => {
            this.routesByJobMap[id] = r;
          })
        })
      }
    }

    if (changes.routes || changes.jobs || changes.highlightedActivity) {
      this._precomputeRouteIndexesAndColors();
    }

    if (changes.jobs && this.jobs?.length) {
      this.viewport?.checkViewportSize();
    }

    this.cdr.detectChanges();
  }

  private _precomputeRouteIndexesAndColors() {
    this.routeIndexesMap = {};
    this.routeColorsMap = {};
    if (!this.jobs?.length) {
      return;
    }
    this.jobs.forEach((job) => {
      const route = this.routesByJobMap?.[job.id];
      if (route) {
        const color = this.planningCommonService.getRouteColor(route);
        if (color) {
          this.routeColorsMap[job.id] = color;
        }

        const indexesInRoute = [];
        if (route.activities?.length) {
          route.activities.forEach((a, i) => {
            const jobIds = a.jobIds || (a.jobId ? [a.jobId] : []);
            if (_.includes(jobIds, job.id)) {
              indexesInRoute.push({
                index: (a.indexInRoute || i),
                isHighlighted: a.id === this.highlightedActivity?.id
              });
            }
          });
        }
        this.routeIndexesMap[job.id] = indexesInRoute;
      }
    });
  }

  trackById = (_: number, job: { id: string | number }) => job.id;

  onToggleSelectedJob(jobIds: string[], isSelected: boolean) {
    if (!jobIds) {
      return;
    }
    let selectedIds;

    if (isSelected) {
      selectedIds = _.uniq([...this.selectedIds].concat(jobIds));
    } else {
      selectedIds = this.selectedIds.filter(id => !jobIds.includes(id));
    }
    this.onSelect.emit(_.compact(selectedIds));
  }


  drop(event: CdkDragDrop<string[]>) {
    this.planningDragAndDropService.onDropJobs(
      event,
      this.selectedIds
    ).then((jobIds) => {
      if (jobIds) {
        this.onMoveToLeftovers.emit(jobIds);
      }
    });
  }

  private _initSelectedKeys() {
    this.selectedKeys = {};
    this.jobs?.forEach(job => {
      if (job.groupedByType) {
        this.selectedKeys[job.id] = !!job.jobIds.every(item => this.selectedIds.includes(item));
      } else {
        if (this.selectedIds.includes(job.id)) {
          this.selectedKeys[job.id] = true;
        }
      }
    })
  }

  onRowDoubleClick(event: MouseEvent, job: any) {
    const route = this.routesByJobMap?.[job.id];
    if (route) {
      this.onOpenRouteInFloatingTable.emit(route.id);
    }
  }

  getRouteIndexes(job: any): { index: number, isHighlighted: boolean }[] {
    return this.routeIndexesMap[job.id] || [];
  }

  getRouteColor(job: any): string | undefined {
    return this.routeColorsMap[job.id];
  }

}
